import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import img1 from '../../assets/img/skybox/interstellar_ft.png';
import img2 from '../../assets/img/skybox/interstellar_bk.png';
import img3 from '../../assets/img/skybox/interstellar_up.png';
import img4 from '../../assets/img/skybox/interstellar_dn.png';
import img5 from '../../assets/img/skybox/interstellar_rt.png';
import img6 from '../../assets/img/skybox/interstellar_lf.png';

import partTexture from '../../assets/img/textures/spark.png';
import Terrain from './Terrain';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/0a191f-dad2d8-0f8b8d-ec9a29-a8201a');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.scene.background = this.colourArr[0];
        this.scene.fog = new THREE.Fog(this.colourArr[2], 1, 9);

        this.setupCamera();
        this.setupLight();
        this.setupControls();
        this.setupTerrain();
        this.createSkyboxFromTexture();
        this.setupGlobalUniforms();
        this.setupGeometry();
        this.setupParticles();
    }

    setupCamera() {
        let VIEW_ANGLE = 60;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.01;
        let FAR_PLANE = 100;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(0.01, 0, 1);
    }

    setupLight() {
        let pointLight = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight.position.set(2.5, 0.2, 2.5);
        this.scene.add(pointLight);

        let pointLight2 = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight2.position.set(-2.5, 0, -2.5);
        this.scene.add(pointLight2);

        let pointLight3 = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight3.position.set(0.5, -0.2, 0);
        this.scene.add(pointLight3);

        let hemiLight = new THREE.HemisphereLight(this.colourArr[1], this.colourArr[4], 1);
        hemiLight.position.set(0, 0.5, 0);
        this.scene.add(hemiLight);
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 0.5;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 4;
        this.controls.minDistance = 0.01;
    }

    setupTerrain() {
        let terrain = new Terrain(this.scene, this.colourArr[1]);
        terrain.setup();
    }

    setupGlobalUniforms() {
        let texLoader = new THREE.TextureLoader();
        let particleMap = texLoader.load(partTexture);
        this.uniforms = {
            uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
            uTime: { value: 0 },
            uTexture0: { value: particleMap }
        };
    }

    createSkyboxFromTexture() {
        let skyboxMatArr = [];
        let loader = new THREE.TextureLoader();
        let skyboxTexFront = loader.load(img1);
        let skyboxTexBack = loader.load(img2);
        let skyboxTexUp = loader.load(img3);
        let skyboxTexDown = loader.load(img4);
        let skyboxTexRight = loader.load(img5);
        let skyboxTexLeft = loader.load(img6);

        skyboxMatArr.push(new THREE.MeshBasicMaterial({ map: skyboxTexFront }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ map: skyboxTexBack }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ map: skyboxTexUp }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ map: skyboxTexDown }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ map: skyboxTexRight }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ map: skyboxTexLeft }));

        let skyboxGeo = new THREE.BoxBufferGeometry(1, 1, 1);
        skyboxGeo.scale(10, 10, -10);

        let skyboxMesh = new THREE.Mesh(skyboxGeo, skyboxMatArr);
        this.scene.add(skyboxMesh);
    }

    setupMirrorMaterial() {
        // let cubeRenderTarget = new THREE.WebGLCubeRenderTarget( 256, { format: THREE.RGBFormat, generateMipmaps: true } );
        // this.mirrorSphereCamera = new THREE.CubeCamera(0.01, 100, cubeRenderTarget);
        this.mirrorSphereCamera = new THREE.CubeCamera(0.01, 100, 512);
        // mirrorCubeCamera.renderTarget.minFilter = THREE.LinearMipMapLinearFilter;
        this.scene.add(this.mirrorSphereCamera);
        let mirrorSphereMaterial = new THREE.MeshBasicMaterial({ envMap: this.mirrorSphereCamera.renderTarget });
        return mirrorSphereMaterial;
    }

    updateMirrorMaterial() {
        this.mirrorSphereCamera.update(this.renderer, this.scene);
    }

    setupParticleMaterial() {
        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                uniform float uTime;
                attribute float size;
                varying vec3 vColor;
            
                void main()
                {
                    vColor = color;
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
		            gl_PointSize = size * (0.3 / -mvPosition.z); //0.3 = camera position.z
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                precision mediump float;

                varying vec3 vColor;
                uniform sampler2D uTexture0;
                
                void main()
                {
                    gl_FragColor = vec4(vColor, 1.0);
		            gl_FragColor = gl_FragColor * texture2D(uTexture0, gl_PointCoord);
                }
            `,
            blending: THREE.AdditiveBlending,
            depthTest: false,
            transparent: true,
            vertexColors: true
        });
        return material;
    }

    setupParticles() {
        //let radius = 10;
        let particleNum = 300;
        let buffGeo = new THREE.BufferGeometry();
        let positions = [];
        let colors = [];
        let sizes = [];

        let color = new THREE.Color();
        for (let i = 0; i < particleNum; i++) {
            positions.push((Math.random() * 2 - 1)); //* radius); 
            positions.push((Math.random() * 2 - 1));// * radius);
            positions.push((Math.random() * 2 - 1)); //* radius);

            // color.setHSL(i/particleNum, 1.0, 0.5);
            color.set(this.colourArr[1]);
            colors.push(color.r, color.g, color.b);

            sizes.push(40);
        }

        buffGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        buffGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        buffGeo.setAttribute('size', new THREE.Float32BufferAttribute(sizes, 1).setUsage(THREE.DynamicDrawUsage));

        let material = this.setupParticleMaterial();
        let particles = new THREE.Points(buffGeo, material);
        this.scene.add(particles);

        this.particles = particles;
        this.buffGeo = buffGeo;
    }

    updateParticles(time) {
        this.particles.rotation.z = 0.01 * time;
        this.particles.position.y -= 0.0001;
        if (this.particles.position.y < -1.7) this.particles.position.y += 4;
        let sizes = this.buffGeo.attributes.size.array;
        for (let i = 0; i < this.particles; i++) {
            sizes[i] = 10 * (1 + Math.sin(0.1 * i + time));
        }
        this.buffGeo.attributes.size.needsUpdate = true;
    }

    setupGeometry() {
        this.meshesArr = [];
        let geo1 = new THREE.IcosahedronBufferGeometry(0.1, 0);
        let geo2 = new THREE.SphereBufferGeometry(0.1, 32, 32);
        let mirrorMat = this.setupMirrorMaterial();
        let mesh1 = new THREE.Mesh(geo1, mirrorMat);
        let mesh2 = new THREE.Mesh(geo2, mirrorMat);

        for (let i = 0; i < 100; i++) {
            const xOffset = getRandom(-3, 3);
            const yOffset = getRandom(-0.3, 0.9);
            const zOffset = getRandom(-3, 3);

            const mesh = Math.random() > 0.5 ? mesh1.clone() : mesh2.clone();
            mesh.rotation.y = getRandom(0, Math.PI * 2);
            mesh.position.set(xOffset, yOffset, zOffset);
            mesh.scaleFactor = getRandom(0.1, 1.9);
            mesh.scale.multiplyScalar(mesh.scaleFactor);
            this.meshesArr.push(mesh);
            this.scene.add(mesh);
        };
    }

    orbitCamera(rotSpeed) {
        let x = this.camera.position.x;
        let z = this.camera.position.z;

        this.camera.position.x += x * Math.cos(rotSpeed) + z * Math.sin(rotSpeed);
        this.camera.position.z += z * Math.cos(rotSpeed) - x * Math.sin(rotSpeed);

        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    updateSceneSubjects(time) {
        this.updateParticles(time);
        this.updateMirrorMaterial();

        for (let i = 0; i < this.meshesArr.length; i++) {
            this.meshesArr[i].rotation.y += 0.005;
        }
    }

    update() {
        if (this.disposed) return;
        if (this.controls) this.controls.update();
        this.updateSceneSubjects(Date.now() * 0.005);
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                // child.material.dispose(); //TODO: fix mistake with this line
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
    };
}

function getRandom(min, max) {
    return Math.random() * (max - min) + min;
}
